import React, { useState, useEffect } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { paymentService } from '../../services/payments';
import Button from '../ui/Button';
import AppIcon from '../AppIcon';

const StripePaymentFormInner = ({
  clientSecret,
  amount,
  currency = 'GBP',
  orderData,
  customerInfo,
  onSuccess,
  onError,
  confirmButtonText = 'Complete Payment',
  className = ''
}) => {
  const stripe = useStripe();
  const elements = useElements();
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [paymentReady, setPaymentReady] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    setErrorMessage('');
    setPaymentReady(false);
  }, [clientSecret]);

  useEffect(() => {
    if (elements) {
      const paymentElement = elements?.getElement(PaymentElement);
      if (paymentElement) {
        paymentElement?.on('ready', () => {
          setPaymentReady(true);
        });
        
        paymentElement?.on('change', (event) => {
          if (event?.error) {
            setErrorMessage(event?.error?.message);
          } else {
            setErrorMessage('');
          }
        });
      }
    }
  }, [elements]);

  const handleSubmit = async (event) => {
    event?.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      setErrorMessage('Payment system not ready. Please try again.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const { orderId, orderNumber } = orderData || {};

      const { error: stripeError, paymentIntent } = await stripe?.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location?.origin}/payment-success`,
        },
        redirect: 'if_required'
      });

      if (stripeError) {
        let userFriendlyMessage = 'Payment failed. Please try again.';
        
        switch (stripeError?.code) {
          case 'card_declined':
            userFriendlyMessage = 'Your card was declined. Please try a different payment method.';
            break;
          case 'insufficient_funds':
            userFriendlyMessage = 'Insufficient funds. Please check your account balance.';
            break;
          case 'expired_card':
            userFriendlyMessage = 'Your card has expired. Please use a different card.';
            break;
          case 'incorrect_cvc':
            userFriendlyMessage = 'Incorrect security code. Please check your card details.';
            break;
          case 'processing_error':
            userFriendlyMessage = 'Processing error. Please try again in a moment.';
            break;
          default:
            userFriendlyMessage = stripeError?.message || 'Payment failed. Please try again.';
        }
        
        setErrorMessage(userFriendlyMessage);
        onError?.(stripeError);
      } else if (paymentIntent) {
        if (paymentIntent?.status === 'succeeded') {
          try {
            const confirmData = await paymentService?.confirmPayment(paymentIntent?.id);
            
            const successResult = {
              paymentIntent,
              orderId,
              orderNumber,
              orderData: confirmData?.booking,
              bookingConfirmed: true
            };

            setSuccessData(successResult);
            setShowSuccessAnimation(true);

            setTimeout(() => {
              setShowSuccessAnimation(false);
              onSuccess?.(successResult);
            }, 2500);
          } catch (confirmError) {
            console.warn('Payment succeeded but confirmation failed:', confirmError);
            const successResult = {
              paymentIntent,
              orderId,
              orderNumber,
              warning: 'Payment processed successfully, but confirmation is pending.',
              bookingConfirmed: false
            };
            
            setSuccessData(successResult);
            setShowSuccessAnimation(true);
            
            setTimeout(() => {
              setShowSuccessAnimation(false);
              onSuccess?.(successResult);
            }, 2500);
          }
        } else if (paymentIntent?.status === 'requires_action') {
          setErrorMessage('Additional authentication required. Please complete the verification.');
        } else {
          setErrorMessage('Payment processing incomplete. Please try again.');
        }
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      setErrorMessage('An unexpected error occurred. Please try again.');
      onError?.(error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (!stripe || !elements || !clientSecret) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
        <p className="text-sm text-gray-500 mt-4 text-center">
          Loading secure payment system...
        </p>
      </div>
    );
  }

  return (
    <div className={`relative bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <PaymentElement 
            options={{
              fields: {
                billingDetails: 'auto'
              },
              layout: {
                type: 'tabs',
                defaultCollapsed: false
              }
            }}
          />
        </div>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AppIcon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-800 text-sm font-medium">Payment Error</p>
              <p className="text-red-700 text-sm mt-1">{errorMessage}</p>
            </div>
          </div>
        )}

        <Button
          type="submit"
          disabled={!paymentReady || isProcessing || !stripe || !elements}
          className="w-full"
          iconName={isProcessing ? "Loader2" : "CreditCard"}
          iconClassName={isProcessing ? "animate-spin" : ""}
          iconPosition="left"
        >
          {isProcessing ? 'Processing Payment...' : confirmButtonText}
        </Button>

        <div className="flex items-center justify-center space-x-2 text-xs text-gray-500">
          <AppIcon name="Shield" size={14} />
          <span>Secured by Stripe • Your payment information is encrypted</span>
        </div>
      </form>

      {showSuccessAnimation && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-lg flex items-center justify-center z-50">
          <div className="text-center space-y-4 p-8">
            <div className="relative">
              <div className="w-20 h-20 mx-auto bg-green-100 rounded-full flex items-center justify-center animate-pulse">
                <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                  <AppIcon name="Check" size={32} className="text-white" />
                </div>
              </div>
              <div className="absolute inset-0 w-20 h-20 mx-auto border-4 border-green-300 rounded-full animate-ping opacity-30"></div>
            </div>

            <div className="space-y-2">
              <h3 className="text-2xl font-bold text-green-600">
                Payment Successful! 🎉
              </h3>
              <p className="text-green-700 font-medium">
                Processing your request...
              </p>
            </div>

            <div className="flex justify-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StripePaymentFormInner;